import {
  Button,
  Card,
  CardContent,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Typography,
} from "@/components/ui";
import {
  formatTemplateDictionaryTitle,
  type TemplateDictionaryItem,
} from "@/lib/data/static/templateDictionaryItems";
import { encodeTemplateUserRoleLabel } from "@/lib/utils/templateUserRoleVariable";
import { camelCase, snakeCase, startCase } from "lodash";
import { Check, ChevronDown, Copy, Plus } from "lucide-react";
import pluralize from "pluralize";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

export type StringFormatOption =
  | "none"
  | "upperCase"
  | "lowerCase"
  | "titleCase"
  | "plural"
  | "possessive";

export type DateFormatOption = "none" | "short" | "long";

export type FormatOption = StringFormatOption | DateFormatOption;

export interface MatterTemplateDictionaryCardState {
  customFieldValue: string;
  format: FormatOption;
  instanceNickname: string;
  instanceNumber: number;
  selectedOption: string;
  signerFieldHeight: string;
  signerFieldWidth: string;
  signerUnderline: boolean;
}

export const getMatterTemplateDictionaryCardStateKey = (
  item: TemplateDictionaryItem,
) => `${item.parent}:${item.variable}`;

export const getDefaultMatterTemplateDictionaryCardState = (
  item: TemplateDictionaryItem,
  selectedContactType?: string,
): MatterTemplateDictionaryCardState => ({
  customFieldValue: "",
  format: "none",
  instanceNickname: "",
  instanceNumber: 1,
  selectedOption: selectedContactType || item.options?.[0] || "",
  signerFieldHeight: "",
  signerFieldWidth: "",
  signerUnderline: false,
});

interface MatterTemplateDictionaryCardProps {
  item: TemplateDictionaryItem;
  onCopy: (text: string) => void;
  selectedContactType?: string;
  onSelect?: (text: string) => void;
  selectLabel?: string;
  state?: Partial<MatterTemplateDictionaryCardState>;
  onStateChange?: (state: MatterTemplateDictionaryCardState) => void;
  userRoleLabels?: string[];
}

export const MatterTemplateDictionaryCard = ({
  item,
  onCopy,
  selectedContactType,
  onSelect,
  selectLabel = "Insert",
  state,
  onStateChange,
  userRoleLabels,
}: MatterTemplateDictionaryCardProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isAdvancedLayoutOpen, setIsAdvancedLayoutOpen] = useState(false);
  const [localState, setLocalState] =
    useState<MatterTemplateDictionaryCardState>(() => ({
      ...getDefaultMatterTemplateDictionaryCardState(item, selectedContactType),
      ...state,
    }));
  const resolvedState = useMemo(
    () => ({
      ...getDefaultMatterTemplateDictionaryCardState(item, selectedContactType),
      ...localState,
      ...state,
    }),
    [item, selectedContactType, localState, state],
  );
  const {
    customFieldValue,
    format,
    instanceNickname,
    instanceNumber,
    selectedOption,
    signerFieldHeight,
    signerFieldWidth,
    signerUnderline,
  } = resolvedState;

  useEffect(() => {
    setLocalState((prev) => ({
      ...getDefaultMatterTemplateDictionaryCardState(item, selectedContactType),
      ...prev,
      ...state,
    }));
  }, [item, selectedContactType, state]);

  const allowInstanceNickname = item.allowInstanceNickname === true;
  const isFirmUserVariable = item.parent === "firmUser";
  const isNumberType = item.type === "number";
  const isDateType = item.type === "date";
  const hasOptions = item.options && item.options.length > 0;
  const isSignatureVariable = item.isSignatureVariable ?? false;
  const isSignerVariable =
    item.parent === "signer" || item.parent === "userSigner";
  const supportsUnderline = isSignerVariable;
  const requiresCustomInput = item.requiresCustomInput ?? false;
  const customInputLabel = item.customInputLabel ?? "Field Name";
  const customInputPlaceholder =
    item.customInputPlaceholder ?? "Custom field name";
  const customInputFallback = item.customInputFallback ?? "";
  const sanitizedCustomFieldName = customFieldValue.trim()
    ? isSignerVariable
      ? camelCase(customFieldValue.trim())
      : snakeCase(customFieldValue.trim())
    : "";
  const hasFormattingInFieldName =
    isSignerVariable &&
    requiresCustomInput &&
    /(?:^|[\s_])(?:w\d{1,3}h\d{1,3}|underline)$/i.test(customFieldValue.trim());
  const hasSignerSize = Boolean(signerFieldWidth || signerFieldHeight);
  const isValidSignerSize =
    !hasSignerSize ||
    [signerFieldWidth, signerFieldHeight].every((value) => {
      const number = Number(value);
      return (
        value !== "" && Number.isInteger(number) && number >= 1 && number <= 100
      );
    });
  const isCopyDisabled =
    (requiresCustomInput && !sanitizedCustomFieldName) ||
    hasFormattingInFieldName ||
    !isValidSignerSize;

  const updateState = useCallback(
    (nextState: Partial<MatterTemplateDictionaryCardState>) => {
      setLocalState((prev) => ({
        ...prev,
        ...nextState,
      }));
      onStateChange?.({
        ...resolvedState,
        ...nextState,
      });
    },
    [onStateChange, resolvedState],
  );

  useEffect(() => {
    if (
      selectedContactType &&
      hasOptions &&
      selectedOption !== selectedContactType
    ) {
      updateState({ selectedOption: selectedContactType });
    }
  }, [hasOptions, selectedContactType, selectedOption, updateState]);

  const sanitizeNickname = useCallback(
    (value: string) => {
      if (isFirmUserVariable) {
        return encodeTemplateUserRoleLabel(value);
      }
      const trimmed = value.trim();
      if (!trimmed) return "";
      return startCase(trimmed).replace(/[^A-Za-z0-9]/g, "");
    },
    [isFirmUserVariable],
  );

  const sanitizedInstanceNickname = useMemo(
    () => (allowInstanceNickname ? sanitizeNickname(instanceNickname) : ""),
    [instanceNickname, allowInstanceNickname, sanitizeNickname],
  );
  const hasNickname = allowInstanceNickname && !!sanitizedInstanceNickname;
  const instanceLabel = hasNickname
    ? isFirmUserVariable
      ? instanceNickname.trim()
      : startCase(sanitizedInstanceNickname)
    : "";
  const instanceDisplayLabel = hasNickname
    ? ` (${instanceLabel})`
    : instanceNumber > 1
      ? ` (Instance ${instanceNumber})`
      : "";

  const getFormatSuffix = (format: FormatOption) => {
    if (isNumberType) return "";
    switch (format) {
      case "upperCase":
        return "_upperCase";
      case "lowerCase":
        return "_lowerCase";
      case "titleCase":
        return "_titleCase";
      case "plural":
        return "_plural";
      case "possessive":
        return "_possessive";
      case "short":
        return "_short";
      case "long":
        return "_long";
      default:
        return "";
    }
  };

  const getFormattedVariable = (variable: string) => {
    const defaultOption = item.options?.[0];
    const baseVariable =
      hasOptions && defaultOption && selectedOption !== defaultOption
        ? variable.replace(defaultOption, selectedOption)
        : variable;
    const nicknameSuffix =
      hasNickname && sanitizedInstanceNickname
        ? `_${sanitizedInstanceNickname}`
        : "";

    if (isSignerVariable) {
      let formatSuffix = getFormatSuffix(format);
      const instanceSuffix = `${instanceNumber}`;
      const prefix = item.parent === "userSigner" ? "UserSigner" : "Signer";
      const signerVariable = baseVariable.replace(`${prefix}_`, "");
      const [fieldTypeRaw, ...fieldNameParts] = signerVariable.split("_");
      let fieldType = fieldTypeRaw || "field";
      const defaultFieldName = fieldNameParts.join("_");
      const resolvedFieldName = (() => {
        if (requiresCustomInput) {
          if (sanitizedCustomFieldName) {
            return sanitizedCustomFieldName;
          }
          if (defaultFieldName) {
            return defaultFieldName;
          }
          if (customInputFallback) {
            return customInputFallback;
          }
          return "fieldName";
        }
        return defaultFieldName;
      })();
      if (fieldType === "date") {
        if (format === "short") {
          fieldType = "dateShort";
          formatSuffix = "";
        } else if (format === "long") {
          fieldType = "dateLong";
          formatSuffix = "";
        }
      }
      const finalVariable =
        resolvedFieldName && (fieldNameParts.length > 0 || requiresCustomInput)
          ? `${fieldType}_${resolvedFieldName}`
          : fieldType;
      const sizeSuffix =
        hasSignerSize && isValidSignerSize
          ? `_w${Number(signerFieldWidth)}h${Number(signerFieldHeight)}`
          : "";
      const underlineSuffix =
        supportsUnderline && signerUnderline ? "_underline" : "";
      return `{{${prefix}${instanceSuffix}_${finalVariable}${formatSuffix}${sizeSuffix}${underlineSuffix}}}`;
    }

    // Handle other variables normally
    const formatSuffix = getFormatSuffix(format);
    const shouldAppendInstance = instanceNumber > 1 && !hasNickname;
    const instanceSuffix = shouldAppendInstance ? `_${instanceNumber}` : "";
    return `{{${baseVariable}${nicknameSuffix}${formatSuffix}${instanceSuffix}}}`;
  };

  const copyToClipboard = async (variable: string) => {
    if (isCopyDisabled) {
      toast.error(
        !isValidSignerSize
          ? "Enter a width and height from 1 to 100."
          : hasFormattingInFieldName
            ? "Set size and underline below, outside the field name."
            : "Enter a field name before copying.",
      );
      return;
    }

    const formattedVariable = getFormattedVariable(variable);

    if (isSignatureVariable) {
      // For signature variables, copy as HTML with signature font styling
      const htmlContent = `<span style="font-family: 'Pacifico', cursive, serif; font-size: 1rem; line-height: 1.5rem; text-align: center; color: #374151;">${formattedVariable}</span>`;

      // Copy as HTML to clipboard
      const clipboardItem = new ClipboardItem({
        "text/html": new Blob([htmlContent], { type: "text/html" }),
        "text/plain": new Blob([formattedVariable], { type: "text/plain" }),
      });

      try {
        await navigator.clipboard.write([clipboardItem]);
        setIsCopied(true);
        toast.success("Copied to clipboard!");
        setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      } catch (error) {
        console.error("Failed to copy to clipboard:", error);
        // Fallback to plain text copy
        onCopy(formattedVariable);
        setIsCopied(true);
        toast.success("Copied to clipboard!");
        setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      }
    } else {
      // For regular variables, just copy the variable
      onCopy(formattedVariable);
      setIsCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    }
  };

  const handleFormatChange = (newFormat: FormatOption) => {
    updateState({ format: newFormat });
  };

  const handleInstanceChange = (newInstance: number) => {
    updateState({ instanceNumber: newInstance });
  };

  const handleSelect = () => {
    if (!onSelect) return;
    if (isCopyDisabled) {
      toast.error(
        !isValidSignerSize
          ? "Enter a width and height from 1 to 100."
          : hasFormattingInFieldName
            ? "Set size and underline below, outside the field name."
            : "Enter a field name before inserting.",
      );
      return;
    }
    onSelect(getFormattedVariable(item.variable));
  };

  const getExampleValue = (format: FormatOption) => {
    if (isNumberType) return item.exampleValue ?? "12345";
    if (requiresCustomInput && !isDateType && customFieldValue.trim()) {
      return customFieldValue.trim();
    }
    if (isDateType) {
      const date = new Date();
      switch (format) {
        case "short":
          return date.toLocaleDateString("en-US", {
            month: "numeric",
            day: "numeric",
            year: "numeric",
          });
        case "long":
          return date.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          });
        default:
          return date.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
      }
    }
    const example = item.exampleValue ?? "Example Value";
    switch (format) {
      case "upperCase":
        return example.toUpperCase();
      case "lowerCase":
        return example.toLowerCase();
      case "titleCase":
        return startCase(example);
      case "plural":
        return pluralize(example);
      case "possessive":
        const words = example.split(" ");
        const lastWord = words[words.length - 1];
        words[words.length - 1] = lastWord.endsWith("s")
          ? lastWord + "'"
          : lastWord + "'s";
        return words.join(" ");
      default:
        return example;
    }
  };

  const getFormatOptions = () => {
    if (isNumberType) return [];
    if (isDateType) {
      return [
        { value: "none", label: "No format" },
        { value: "short", label: "Short date" },
        { value: "long", label: "Long date" },
      ];
    }
    return [
      { value: "none", label: "No format" },
      { value: "upperCase", label: "UPPERCASE" },
      { value: "lowerCase", label: "lowercase" },
      { value: "titleCase", label: "Title case" },
      { value: "plural", label: "Plural" },
      { value: "possessive", label: "Possessive" },
    ];
  };

  const getDisplayTitle = () => {
    if (
      !hasOptions ||
      !selectedContactType ||
      selectedContactType === "contact"
    ) {
      return item.title;
    }
    return formatTemplateDictionaryTitle(
      item.title.replace("Contact", startCase(selectedContactType)),
    );
  };

  return (
    <Card className="overflow-hidden">
      <CardTitle className="bg-light-primary p-2">
        <div className="flex items-center justify-between gap-2">
          <Typography size="lg" variant="default">
            {getDisplayTitle()}
          </Typography>
        </div>
      </CardTitle>
      <CardContent className="p-3">
        <div className="flex flex-col gap-3">
          {/* Variable Display Section */}
          <div className="flex flex-col gap-1">
            <Typography
              variant="subHeading"
              size="sm"
              className="text-muted-foreground"
            >
              Variable
            </Typography>
            <div className="flex items-center gap-2 rounded-md border p-1.5">
              <div className="scrollbar-hide min-w-0 flex-1 overflow-x-auto">
                {isSignatureVariable ? (
                  <div
                    className="text-foreground text-center"
                    style={{
                      fontFamily: "'Pacifico', cursive, serif",
                      fontSize: "1rem",
                      lineHeight: "1.5rem",
                    }}
                  >
                    {getFormattedVariable(item.variable)}
                  </div>
                ) : (
                  <Typography
                    variant="subHeading"
                    className="font-mono whitespace-nowrap"
                  >
                    {getFormattedVariable(item.variable)}
                  </Typography>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 flex-shrink-0 rounded-full"
                disabled={isCopyDisabled}
                onClick={() => {
                  void copyToClipboard(item.variable);
                }}
              >
                {isCopied ? (
                  <Check className="animate-fade-in h-3.5 w-3.5 text-green-500" />
                ) : (
                  <Copy className="animate-fade-in h-3.5 w-3.5" />
                )}
              </Button>
              {onSelect && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 flex-shrink-0 rounded-full"
                  disabled={isCopyDisabled}
                  onClick={handleSelect}
                  aria-label={selectLabel}
                  title={selectLabel}
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>

          {requiresCustomInput && (
            <div className="flex flex-col gap-1">
              <Typography
                variant="subHeading"
                size="sm"
                className="text-muted-foreground"
              >
                {customInputLabel}
              </Typography>
              <Input
                value={customFieldValue}
                onChange={(event) => {
                  updateState({ customFieldValue: event.target.value });
                }}
                placeholder={customInputPlaceholder}
              />
              {hasFormattingInFieldName && (
                <Typography className="text-destructive text-xs">
                  Remove formatting from the field name. Use Advanced layout for
                  size and underline.
                </Typography>
              )}
            </div>
          )}

          {isSignerVariable && (
            <div className="flex flex-col gap-2">
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground flex w-fit items-center gap-1 text-sm"
                aria-expanded={isAdvancedLayoutOpen}
                onClick={() => {
                  setIsAdvancedLayoutOpen((open) => !open);
                }}
              >
                Advanced layout
                {(hasSignerSize || signerUnderline) && " (configured)"}
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${isAdvancedLayoutOpen ? "rotate-180" : ""}`}
                />
              </button>
              {isAdvancedLayoutOpen && (
                <div className="flex flex-col gap-2">
                  <Typography
                    variant="subHeading"
                    size="sm"
                    className="text-muted-foreground"
                  >
                    Field size (optional)
                  </Typography>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="flex flex-col gap-1 text-sm">
                      Width (%)
                      <Input
                        type="number"
                        min={1}
                        max={100}
                        step={1}
                        value={signerFieldWidth}
                        onChange={(event) => {
                          updateState({ signerFieldWidth: event.target.value });
                        }}
                        placeholder="Default"
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-sm">
                      Height (%)
                      <Input
                        type="number"
                        min={1}
                        max={100}
                        step={1}
                        value={signerFieldHeight}
                        onChange={(event) => {
                          updateState({
                            signerFieldHeight: event.target.value,
                          });
                        }}
                        placeholder="Default"
                      />
                    </label>
                  </div>
                  {!isValidSignerSize && (
                    <Typography className="text-destructive text-xs">
                      Enter both width and height as whole numbers from 1 to
                      100.
                    </Typography>
                  )}
                  {supportsUnderline && (
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={signerUnderline}
                        onChange={(event) => {
                          updateState({
                            signerUnderline: event.target.checked,
                          });
                        }}
                      />
                      Underline signed value
                    </label>
                  )}
                  <Typography className="text-muted-foreground text-xs">
                    Keep the variable on one line in your template.
                  </Typography>
                </div>
              )}
            </div>
          )}

          {/* Example output Section */}
          <div className="flex flex-col gap-1">
            <Typography
              variant="subHeading"
              size="sm"
              className="text-muted-foreground"
            >
              Example output
            </Typography>
            <div className="bg-muted/50 rounded-md p-2">
              {isSignatureVariable ? (
                <div
                  className="text-foreground text-center"
                  style={{
                    fontFamily: "'Pacifico', cursive, serif",
                    fontSize: "1rem",
                    lineHeight: "1.5rem",
                    textDecoration: signerUnderline ? "underline" : undefined,
                  }}
                >
                  {getExampleValue(format)}
                  {instanceDisplayLabel}
                </div>
              ) : (
                <Typography
                  className={`text-muted-foreground ${supportsUnderline && signerUnderline ? "underline" : ""}`}
                >
                  {getExampleValue(format)}
                  {instanceDisplayLabel}
                </Typography>
              )}
            </div>
          </div>

          {/* Controls Section */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                {!isNumberType && !item.hideFormatting && (
                  <>
                    <Typography
                      variant="subHeading"
                      size="xs"
                      className="bg-card absolute -top-2 left-2 px-1"
                    >
                      Format
                    </Typography>
                    <select
                      value={format}
                      onChange={(event) => {
                        handleFormatChange(event.target.value as FormatOption);
                      }}
                      className="border-input bg-card ring-offset-background focus:ring-ring h-9 w-full rounded-md border px-3 text-sm focus:ring-2 focus:ring-offset-2 focus:outline-hidden"
                    >
                      {getFormatOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>
              <div className={`relative ${isNumberType ? "flex-1" : "flex-1"}`}>
                <Typography
                  variant="subHeading"
                  size="xs"
                  className="bg-card absolute -top-2 left-2 px-1"
                >
                  Instance
                </Typography>
                <div className="border-input bg-card flex h-9 items-center justify-between rounded-md border px-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2"
                    onClick={() => {
                      handleInstanceChange(instanceNumber - 1);
                    }}
                    disabled={instanceNumber <= 1}
                  >
                    -
                  </Button>
                  <Typography className="min-w-0 text-sm">
                    {hasNickname ? `(${instanceLabel})` : `#${instanceNumber}`}
                  </Typography>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2"
                    aria-label="Increase instance"
                    onClick={() => {
                      handleInstanceChange(instanceNumber + 1);
                    }}
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
          {allowInstanceNickname && (
            <div className="flex flex-col gap-1 pt-1">
              <Typography
                variant="subHeading"
                size="sm"
                className="text-muted-foreground"
              >
                {isFirmUserVariable
                  ? "Matter Role (optional)"
                  : "Instance Nickname (optional)"}
              </Typography>
              {isFirmUserVariable ? (
                <Select
                  value={instanceNickname || "__none__"}
                  onValueChange={(value) => {
                    updateState({
                      instanceNickname: value === "__none__" ? "" : value,
                    });
                  }}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select a matter role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No role selected</SelectItem>
                    {userRoleLabels?.map((roleLabel) => (
                      <SelectItem key={roleLabel} value={roleLabel}>
                        {roleLabel}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={instanceNickname}
                  onChange={(event) => {
                    updateState({ instanceNickname: event.target.value });
                  }}
                  placeholder="e.g. Attorney"
                />
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
